import React from 'react';
import { GqlClient, GqlClientOptions, graphql, useGqlContext } from '@search/gql-client/src';
import { useGql2Loader } from '@search/gql-client/src/useGql2';
import { Mg2DemandPurposeEnum, RealtyObjectEnum } from '@search/graphql-typings/src';
import { useMinimalMortgageRateQuery$data as MinimalMortgageRateResponse, useMinimalMortgageRateQuery$variables as MinimalMortgageRateVariables } from './__generated__/useMinimalMortgageRateQuery.graphql';

interface UseMinimalMortgageRateProps {
    regionId: number;
    cost?: number;
    propertyType?: Omit<RealtyObjectEnum, RealtyObjectEnum.Unknown>;
    avgPricePerUnit?: number | null;
    isNewBuilding: boolean;
}

const DEFAULT_PROPERTY_COST = 6000000;

export const minimalRateQuery = graphql`
    query useMinimalMortgageRateQuery(
        $purpose: MG2DemandPurposeEnum!,
        $programCode: String!
        $regionId: Int,
        $cost: Float!,
        $downPayment: Float!,
        $loanAmount: Float!
    ) {
        minimalMortgageRate(
            input: {
                demand: {
                  purpose: $purpose,
                  programCode: $programCode,
                  dealRegionId: $regionId,
                  loanTermYears: 30,
                  downPayment: $downPayment,
                  loanAmount: $loanAmount
                },
                property: {
                  regionId: $regionId,
                  cost: $cost
                }
              }
        ) {
            data {
                rate
            }
        }
    }
`;

export function createMinimalMortgageRateFetcher({ gql }: {
    gql: GqlClient;
}) {
    return (vars: MinimalMortgageRateVariables, opts?: GqlClientOptions) => {
        return gql.client<
            MinimalMortgageRateVariables,
            MinimalMortgageRateResponse
        >(minimalRateQuery, {
            ...vars
        }, opts);
    };
}

function useMinimalMortgageRateFetcher() {
    const gql = useGqlContext();

    return React.useCallback(createMinimalMortgageRateFetcher({ gql }), [ gql ]);
}

export function getVariables({ propertyType = RealtyObjectEnum.Flat, avgPricePerUnit, cost = DEFAULT_PROPERTY_COST, isNewBuilding, ...rest }: UseMinimalMortgageRateProps): MinimalMortgageRateVariables {
    const calculatedCost = avgPricePerUnit ? Math.round(avgPricePerUnit * 50 / 100) : cost;
    let purpose = Mg2DemandPurposeEnum.PurposeBuySecondary;

    if (isNewBuilding) {
        purpose = Mg2DemandPurposeEnum.PurposeBuyPrimary;
    }

    if (propertyType === RealtyObjectEnum.Land) {
        purpose = Mg2DemandPurposeEnum.PurposeBuildHouse;
    }

    const downPayment = Math.round(calculatedCost * 0.2);
    const loanAmount = calculatedCost - downPayment;

    return ({
        purpose,
        cost: calculatedCost ?? cost,
        downPayment,
        loanAmount,
        programCode: isNewBuilding ? 'IT_MORTGAGE' : 'BASE',
        ...rest
    });
}

export function useMinimalMortgageRateLoader() {
    const loader = useGql2Loader(useMinimalMortgageRateFetcher(), { cachePrefix: 'MinimalMortgageRate' });

    return React.useCallback((props: UseMinimalMortgageRateProps) => {
        const vars = getVariables(props);
        const res = loader(vars);
        const data = res.data?.minimalMortgageRate.data;
        let rate = null;

        if (data?.rate && typeof data?.rate === 'number') {
            rate = Number(data?.rate.toFixed(1));
        }

        return { rate };
    }, [ loader ]);
}
